import CircleIcon from '@mui/icons-material/Circle'
import {
  Divider,
  ListItemButton,
  ListItemText,
  Stack,
  Typography
} from '@mui/material'
import { Conversation } from '@twilio/conversations'
import React, { FC, useCallback, useContext, useEffect, useState } from 'react'

import { TwilioConversationsContext } from '../../providers'
import { StateToPass } from './ChatPageDetails'
import { SmsParticipantBindings } from './CustomerChatPanel'

interface Props {
  setStateToPass: React.Dispatch<React.SetStateAction<StateToPass | undefined>>
  activeConversationUniqueName: string | undefined
  conversation: Conversation
}

export const ChatPageCard: FC<Props> = ({
  setStateToPass,
  activeConversationUniqueName,
  conversation
}) => {
  const { conversationUniqueNamesWithUnreadMessages, fetchMessagesFlag } =
    useContext(TwilioConversationsContext)

  const [latestConversationMessage, setLatestConversationMessage] =
    useState<string>()

  const [conversationPhoneNumbers, setConversationPhoneNumbers] =
    useState<string>()

  const updateLatestConversationMessage = useCallback(async () => {
    const latestMessages = await conversation.getMessages(1)
    if (!latestMessages || !latestMessages.items) {
      return
    }
    const latestMessage =
      latestMessages.items.length === 1
        ? latestMessages.items.at(0)?.body
        : undefined
    if (latestMessage) {
      setLatestConversationMessage(latestMessage)
    }
  }, [conversation])

  useEffect(() => {
    updateLatestConversationMessage()
  }, [updateLatestConversationMessage, fetchMessagesFlag])

  const updateConversationPhoneNumbers = useCallback(async () => {
    const conversationParticipants = await conversation.getParticipants()
    if (!conversationParticipants) {
      return
    }
    const smsParticipants = conversationParticipants
      .filter((participant) => participant.type === 'sms')
      .map((participant) => {
        const smsBindings = participant.bindings as SmsParticipantBindings
        return '(' + smsBindings.sms.address + ')'
      })
    setConversationPhoneNumbers(smsParticipants.join(' '))
  }, [conversation])

  useEffect(() => {
    updateConversationPhoneNumbers()
  }, [updateConversationPhoneNumbers])

  const displayListItem = () => {
    return (
      <>
        <ListItemButton
          alignItems="center"
          sx={{ width: '100%' }}
          key={conversation.sid}
          onClick={() => {
            setStateToPass({
              activeConversationId: conversation.uniqueName!,
              customerName: conversation.friendlyName?.includes('Customer:')
                ? conversation.friendlyName.substring(10)
                : undefined
            })
          }}>
          {activeConversationUniqueName !== conversation.uniqueName &&
            conversationUniqueNamesWithUnreadMessages.includes(
              conversation.uniqueName!
            ) && (
              <CircleIcon
                color="warning"
                sx={{ fontSize: '16px', marginRight: '10px' }}
              />
            )}
          <ListItemText
            primary={
              <React.Fragment>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h6">
                    {conversation.friendlyName?.startsWith('Customer: ')
                      ? conversation.friendlyName?.replace('Customer: ', '')
                      : conversation.friendlyName}
                  </Typography>
                  <Typography variant="body1">
                    {conversationPhoneNumbers}
                  </Typography>
                </Stack>
              </React.Fragment>
            }
            secondary={
              <React.Fragment>
                <Typography
                  sx={{ display: 'inline' }}
                  component="span"
                  variant="body2"
                  color="text.primary">
                  {latestConversationMessage || 'No messages received yet'}
                </Typography>
              </React.Fragment>
            }
          />
        </ListItemButton>
        <Divider sx={{ width: '100%' }} />
      </>
    )
  }
  return displayListItem()
}
